import {Component, computed, effect, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {map} from 'rxjs';
import {DatePipe, DecimalPipe} from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CustomerProductBillingSummary, InvoiceStateId} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {BillingBadge} from '../../../../components/billing-badge/billing-badge';
import {RouteUri} from '../../../../constants/route-uri';
import {CustomerBillingQueryService} from './services/customer-billing-query.service';

@Component({
  selector: 'elcia-customer-billing',
  imports: [ElciaCard, BillingBadge, DatePipe, DecimalPipe, TranslatePipe],
  providers: [CustomerBillingQueryService],
  templateUrl: './customer-billing.html',
  styleUrl: './customer-billing.scss',
  host: {class: 'flex flex-col gap-4 min-h-0 overflow-y-auto soft-scrollbar'},
})
export class CustomerBilling {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly queryService = inject(CustomerBillingQueryService);
  private readonly translateService = inject(TranslateService);

  private readonly customerId = toSignal(this.route.parent!.params.pipe(map(params => Number(params['id']))));

  private readonly syncCustomerId = effect(() => {
    this.queryService.customerId.set(this.customerId() ?? null);
  });

  protected readonly previewLines = computed(() => this.queryService.previewLines());
  protected readonly billings = computed(() => this.queryService.billings());

  protected readonly previewTotal = computed(() =>
    this.previewLines().reduce((total, line) => total + line.amount, 0));

  protected readonly invoiceFormRoute = '/' + RouteUri.INVOICE_FORM;
  protected readonly invoicesGridRoute = '/' + RouteUri.FINANCES + '/' + RouteUri.INVOICES;

  protected productName(billing: CustomerProductBillingSummary): string {
    const lang = this.translateService.currentLang ?? '';
    if (lang.startsWith('fr')) return billing.productNameFr;
    if (lang.startsWith('nl')) return billing.productNameNl;
    return billing.productNameEn;
  }

  protected isOneShot(billing: CustomerProductBillingSummary): boolean {
    return billing.periodStart === billing.periodEnd;
  }

  // Brouillon -> formulaire d'édition ; sinon (facture) -> grille des factures.
  protected openInvoice(billing: CustomerProductBillingSummary) {
    if (billing.invoiceState === InvoiceStateId.DRAFT)
      this.router.navigate([this.invoiceFormRoute, billing.invoiceId]);
    else
      this.router.navigate([this.invoicesGridRoute]);
  }
}
